import { useEffect, useMemo, useState } from 'react';
import FullAddressSearch from '@molecules/FullAddressSearch/FullAddressSearch';
import { getMostLikelyCartAddress } from '@api/interfaces/cartApi';
import logger from '@logger';
import useFinishSubmission from './useFinishSubmission';
import useCustomRouter from '@hooks/useCustomRouter';
import paths from '@constants/paths';
import useCart from '@hooks/useCart';
import type { AddressData, Suggestion } from '@occ/api-client';

interface Props {
  afterSubmit?: () => void;
  onCancel?: () => void;
}

const HomeDeliveryAddressSelector = ({ afterSubmit, onCancel }: Props) => {
  const { cart } = useCart();
  const [customerAddress, setCustomerAddress] = useState<AddressData | null>(null);
  const [suggestionForYou, setSuggestionForYou] = useState<Suggestion | undefined>(undefined);
  const [hasLoadedCustomerAddress, setHasLoadedCustomerAddress] = useState(false);
  const router = useCustomRouter();
  const isCheckout = useMemo(() => router.pathname === paths.CHECKOUT, [router.pathname]);

  const getCustomerAddress = async (signal: AbortSignal) => {
    try {
      const customerAddressesResponse = await getMostLikelyCartAddress(signal);
      if (customerAddressesResponse.data) {
        setCustomerAddress(customerAddressesResponse.data);
        setSuggestionForYou({
          street: customerAddressesResponse.data.line1,
          street_number: '',
          letter: '',
          postalcode: customerAddressesResponse.data.postalCode,
          locality: customerAddressesResponse.data.town,
        });
      }

      setHasLoadedCustomerAddress(true);
    } catch (e) {
      if (e instanceof Error) {
        logger.error({ error: 'Could not get customerAddresses', message: e?.message });
      }
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    const cartDeliveryAddress = cart?.deliveryAddress;
    if (cartDeliveryAddress?.line1 && cartDeliveryAddress?.postalCode && cartDeliveryAddress?.town) {
      setCustomerAddress(cartDeliveryAddress);
      setSuggestionForYou({
        street: cartDeliveryAddress.line1,
        street_number: '',
        letter: '',
        postalcode: cartDeliveryAddress.postalCode,
        locality: cartDeliveryAddress.town,
      });
      setHasLoadedCustomerAddress(true);
    } else {
      getCustomerAddress(signal);
    }

    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const finishSubmission = useFinishSubmission(isCheckout ? 'checkout' : 'deliveryPicker', afterSubmit);

  return (
    <>
      {hasLoadedCustomerAddress && (
        <FullAddressSearch
          finishSubmission={finishSubmission}
          customerAddress={customerAddress}
          suggestedAddress={suggestionForYou}
        />
      )}
    </>
  );
};

export default HomeDeliveryAddressSelector;
